// src/pages/Register.js - VERSION AVEC DÉTECTION AUTOMATIQUE DU RÔLE
import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Register.css";
import BrowserRecommendation from '../components/BrowserRecommendation';

// 🌍 CONFIGURATION LANGUES & SPÉCIALISATIONS HARMONISÉES
const LANGUES_COMMUNICATION = {
  principales: ["Français", "Anglais", "Allemand", "Espagnol", "Italien"],
  dialectes_berber: ["Tarifit (Rif)", "Tachelhit (Souss)", "Tamazight (Atlas)", "Kabyle"],
  autres: ["Russe", "Portugais", "Néerlandais", "Turc"]
};

const PAYS_ORIGINE = [
  "Maroc", "Tunisie", "Algérie", "Égypte", "Arabie Saoudite", 
  "Jordanie", "Liban", "Syrie", "Palestine", "Irak", "Libye", "Mauritanie", "Autres"
];

// Spécialisations structurées par catégorie
const CATEGORIES_ENSEIGNEMENT = [
  {
    id: 'fusha',
    label: 'Arabe littéraire',
    emoji: '📖',
    sousoptions: [
      'Arabe littéraire — Général',
      'Grammaire et syntaxe',
      'Lecture et écriture',
      'Arabe des affaires',
      'Préparation examens',
      'Culture et littérature'
    ]
  },
  {
    id: 'coran',
    label: 'Coran & Tajwid',
    emoji: '🕌',
    sousoptions: [
      'Lecture du Coran',
      'Tajwid (règles de récitation)',
      'Mémorisation (Hifz)',
      'Exégèse (Tafsir) — initiation'
    ]
  },
  {
    id: 'dialectal',
    label: 'Arabe dialectal',
    emoji: '💬',
    sousoptions: [
      'Darija marocaine',
      'Darija algérienne',
      'Darija tunisienne',
      'Darija libyenne',
      'Arabe égyptien',
      'Arabe levantin (Syrie, Liban, Palestine, Jordanie)',
      'Arabe du Golfe',
      'Arabe irakien',
      'Arabe mauritanien (Hassaniya)'
    ]
  },
  {
    id: 'amazighe',
    label: 'Amazighe / Tamazight',
    emoji: '⵿',
    sousoptions: [
      'Tarifit (Rif — nord Maroc)',
      'Tachelhit (Souss — sud Maroc)',
      'Tamazight central (Moyen Atlas)',
      'Kabyle (Algérie)',
      'Chaoui (Aurès — Algérie)',
      'Mozabite (Ghardaïa — Algérie)',
      'Touareg',
      'Tamazight de Libye',
      'Tamazight de Tunisie'
    ]
  },
  {
    id: 'calligraphie',
    label: 'Calligraphie arabe',
    emoji: '✍️',
    sousoptions: [
      'Calligraphie — initiation',
      'Calligraphie Naskh',
      'Calligraphie Thuluth',
      'Calligraphie Ruqah',
      'Calligraphie Diwani'
    ]
  }
];

const NIVEAUX_ELEVES = ['Enfants 5-10 ans', 'Enfants 11-15 ans', 'Adolescents', 'Adultes', 'Tous niveaux'];
const NIVEAUX_ARABE = ['Débutant', 'Intermédiaire', 'Avancé', 'Tous niveaux'];

function Register() {
  // 🆕 DÉTECTION DU RÔLE DEPUIS L'URL
  const [searchParams] = useSearchParams();
  const roleFromURL = searchParams.get('role'); // "student" ou "professor"
  
  // ✅ ÉTATS EXISTANTS
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [role, setRole] = useState("eleve"); // sera overridé par l'URL
  const [error, setError] = useState("");
  
  // 🆕 NOUVEAUX ÉTATS PROF SEULEMENT
  const [bio, setBio] = useState("");
  const [specialisations, setSpecialisations] = useState([]);
  const [languesCommunication, setLanguesCommunication] = useState(["Français"]);
  const [niveauEleves, setNiveauEleves] = useState([]);
  const [niveauArabe, setNiveauArabe] = useState([]);
  const [experienceAnnees, setExperienceAnnees] = useState(1);
  const [paysOrigine, setPaysOrigine] = useState("Maroc");
  const [prix30min, setPrix30min] = useState(20);
  const [prix60min, setPrix60min] = useState(35);
  const [certifications, setCertifications] = useState([]);
  const [isProf, setIsProf] = useState(false);
  const [etapeProf, setEtapeProf] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();

  // 🆕 EFFET POUR DÉTECTER ET FIXER LE RÔLE DEPUIS L'URL
  useEffect(() => {
    if (roleFromURL === 'student') {
      setRole('eleve');
      setIsProf(false);
    } else if (roleFromURL === 'professor') {
      setRole('prof');
      setIsProf(true);
    }
  }, [roleFromURL]);

  // ✅ FONCTION ÉLÈVE
  const handleRegisterEleve = async () => {

    if (!prenom || !nom || !email || !password || !telephone) {
      setError("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const check = await fetch(`${API_BASE}/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      const result = await check.json();
      if (result.exists) {
        alert("❌ Cet email est déjà utilisé. Veuillez vous connecter ou utiliser un autre email.");
        return;
      }

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: `${prenom} ${nom}`, role: "eleve" },
        },
      });

      if (signUpError) throw signUpError;

      const user = data?.user;
      const userId = user?.id;

      const session = await supabase.auth.getSession();
      const accessToken = session?.data?.session?.access_token;

      if (userId && accessToken) {
        const eleveResponse = await fetch(`${process.env.REACT_APP_API_URL}/eleves`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            nom,
            prenom,
            email,
            telephone,
            created_by: userId
          }),
        });

      }

      alert("📧 Un lien de confirmation a été envoyé à votre email.");
      navigate("/login");

    } catch (err) {
      console.error("❌ [ELEVE] Erreur complète:", err);
      throw err;
    }
  };

  // 🆕 FONCTION PROF
  const handleRegisterProf = async () => {

    if (isSubmitting) {
      return;
    }

    if (!prenom || !nom || !bio || specialisations.length === 0 || !telephone) {
      setError("Veuillez remplir tous les champs obligatoires du profil professeur");
      return;
    }

    if (bio.length < 50) {
      setError("La biographie doit contenir au moins 50 caractères");
      return;
    }

    if (specialisations.length < 2) {
      setError("Veuillez sélectionner au moins 2 spécialisations");
      return;
    }

    setIsSubmitting(true);

    try {
      const check = await fetch(`${process.env.REACT_APP_API_URL || ''}/check-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });

      const result = await check.json();

      if (result.exists) {
        alert("❌ Cet email est déjà utilisé.");
        setIsSubmitting(false);
        return;
      }

      // Inscription Supabase
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { 
            full_name: `${prenom} ${nom}`, 
            role: "prof" 
          },
        },
      });

      if (signUpError) {
        console.error("❌ [PROF] Erreur signUp:", signUpError);
        throw signUpError;
      }

      const userId = data?.user?.id;

      // Créer le profil immédiatement en DB (sans attendre la confirmation email)
      if (userId) {
        try {
          await fetch(`${process.env.REACT_APP_API_URL || ''}/profs/pre-register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              prenom,
              nom,
              email,
              telephone,
              bio,
              specialites: specialisations,
              langues_communication: languesCommunication,
              niveau_eleves: niveauEleves,
              niveau_arabe: niveauArabe,
              experience_annees: experienceAnnees,
              pays_origine: paysOrigine,
              certifications
            })
          });
          console.log('✅ Profil prof pré-enregistré en DB');
        } catch(e) {
          console.warn('⚠️ Pré-enregistrement échoué (non bloquant):', e.message);
        }
      }

      alert("📧 Un lien de confirmation a été envoyé à votre email. Veuillez confirmer votre email puis vous connecter sur l'espace professeur.");
      navigate("/professor-login");

    } catch (err) {
      console.error("❌ [PROF] Erreur complète inscription prof:", err);
      setError(err.message || "Erreur lors de l'inscription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    try {
      if (role === "eleve") {
        await handleRegisterEleve();
      } else {
        await handleRegisterProf();
      }
    } catch (err) {
      console.error("❌ [GLOBAL] Erreur d'inscription :", err);
      setError(err.message || "Erreur lors de l'inscription");
      setIsSubmitting(false);
    }
  };

  const toggleSpecialisation = (spec) => {
    setSpecialisations(prev => 
      prev.includes(spec) 
        ? prev.filter(s => s !== spec)
        : [...prev, spec]
    );
  };

  const toggleLangue = (langue) => {
    setLanguesCommunication(prev => 
      prev.includes(langue) 
        ? prev.filter(l => l !== langue)
        : [...prev, langue]
    );
  };

  const renderEtapeBase = () => (
    <div className="form-section">
      <h3>{role === "prof" ? "🎓 Informations de base" : "📝 Informations personnelles"}</h3>
      
      <div className="input-group">
        <label>Prénom *</label>
        <input 
          value={prenom} 
          onChange={(e) => setPrenom(e.target.value)} 
          placeholder="Votre prénom"
          required 
        />
      </div>

      <div className="input-group">
        <label>Nom *</label>
        <input 
          value={nom} 
          onChange={(e) => setNom(e.target.value)} 
          placeholder="Votre nom de famille"
          required 
        />
      </div>

      <div className="input-group">
        <label>Email *</label>
        <input 
          type="email" 
          value={email} 
          onChange={(e) => setEmail(e.target.value)} 
          placeholder="votre@email.com"
          required 
        />
      </div>

      <div className="input-group">
        <label>Mot de passe *</label>
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          placeholder="Minimum 6 caractères"
          required 
        />
      </div>
      <div className="input-group">
        <label>Téléphone WhatsApp *</label>
        <input 
          type="tel" 
          value={telephone} 
          onChange={(e) => setTelephone(e.target.value)} 
          placeholder="+33 6 12 34 56 78"
        />
      </div>

      {/* 🆕 SÉLECTEUR DE RÔLE CACHÉ SI VIENT DE L'URL */}
      {!roleFromURL && (
        <div className="input-group">
          <label>Je suis :</label>
          <select value={role} onChange={(e) => {
            setRole(e.target.value);
            setIsProf(e.target.value === "prof");
          }}>
            <option value="eleve">🎓 Élève (apprendre l'arabe)</option>
            <option value="prof">👨‍🏫 Professeur (enseigner l'arabe)</option>
          </select>
        </div>
      )}

      {/* 🆕 AFFICHAGE DU RÔLE FIXÉ SI VIENT DE L'URL */}
      {roleFromURL && (
        <div className="role-fixed-display">
          <p>
            {role === "eleve" ? "🎓 Inscription en tant qu'élève" : "👨‍🏫 Inscription en tant que professeur"}
          </p>
        </div>
      )}

      {isProf && (
        <button 
          type="button" 
          className="btn-next"
          onClick={() => setEtapeProf(2)}
          disabled={!prenom || !nom || !email || !password || !telephone}
        >
          Continuer → Profil Professeur
        </button>
      )}
    </div>
  );

  const renderEtapeProf = () => (
    <div className="form-section">
      <div className="etape-header">
        <button type="button" onClick={() => setEtapeProf(1)} className="btn-back">
          ← Retour
        </button>
        <h3>👨‍🏫 Votre profil professeur</h3>
      </div>

      {/* BLOC 1 — Présentation */}
      <div className="prof-bloc">
        <div className="prof-bloc-title">✏️ Présentation</div>

        <div className="input-group">
          <label>Bio courte * <span className="label-hint">(max 150 caractères)</span></label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.substring(0, 150))}
            placeholder="Ex: Professeur d'arabe avec 5 ans d'expérience, passionné par l'enseignement aux enfants de la diaspora..."
            rows={3}
            className={bio.length < 20 && bio.length > 0 ? "invalid" : ""}
          />
          <small style={{color: bio.length >= 150 ? '#e53e3e' : '#718096'}}>{bio.length}/150 caractères</small>
        </div>

        <div className="row">
          <div className="input-group">
            <label>Pays d'origine *</label>
            <select value={paysOrigine} onChange={(e) => setPaysOrigine(e.target.value)}>
              {PAYS_ORIGINE.map(pays => (
                <option key={pays} value={pays}>{pays}</option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label>Années d'expérience *</label>
            <select value={experienceAnnees} onChange={(e) => setExperienceAnnees(Number(e.target.value))}>
              {[1,2,3,4,5,6,7,8,9,10,15,20].map(n => (
                <option key={n} value={n}>{n >= 10 ? n + '+' : n}+ an{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* BLOC 2 — Ce que vous enseignez */}
      <div className="prof-bloc">
        <div className="prof-bloc-title">📚 Ce que vous enseignez *</div>
        <p className="prof-bloc-hint">Sélectionnez vos domaines puis les sous-spécialités</p>

        {CATEGORIES_ENSEIGNEMENT.map(cat => (
          <div key={cat.id} className="categorie-bloc">
            <div className="categorie-header">
              <span className="cat-emoji">{cat.emoji}</span>
              <span className="cat-label">{cat.label}</span>
              {specialisations.some(s => cat.sousoptions.includes(s)) && (
                <span className="cat-count">{specialisations.filter(s => cat.sousoptions.includes(s)).length} ✓</span>
              )}
            </div>
            <div className="sousoptions-grid">
              {cat.sousoptions.map(opt => (
                <button
                  key={opt}
                  type="button"
                  className={`sousopt-btn ${specialisations.includes(opt) ? 'selected' : ''}`}
                  onClick={() => toggleSpecialisation(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        ))}
        <small style={{color: specialisations.length === 0 ? '#e53e3e' : '#38a169'}}>
          {specialisations.length === 0 ? '⚠️ Sélectionnez au moins 1 spécialité' : `✅ ${specialisations.length} spécialité(s) sélectionnée(s)`}
        </small>
      </div>

      {/* BLOC 3 — Vos élèves cibles */}
      <div className="prof-bloc">
        <div className="prof-bloc-title">🎓 Vos élèves cibles *</div>
        <div className="tags-grid">
          {['Enfants 5-10 ans', 'Enfants 11-15 ans', 'Adolescents', 'Adultes', 'Tous niveaux'].map(n => (
            <button
              key={n}
              type="button"
              className={`tag-btn ${niveauEleves.includes(n) ? 'selected' : ''}`}
              onClick={() => setNiveauEleves(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n])}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* BLOC 4 — Niveau enseigné */}
      <div className="prof-bloc">
        <div className="prof-bloc-title">📊 Niveau d'arabe enseigné *</div>
        <div className="tags-grid">
          {['Débutant', 'Intermédiaire', 'Avancé', 'Tous niveaux'].map(n => (
            <button
              key={n}
              type="button"
              className={`tag-btn ${niveauArabe.includes(n) ? 'selected' : ''}`}
              onClick={() => setNiveauArabe(prev => prev.includes(n) ? prev.filter(x => x !== n) : [...prev, n])}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* BLOC 5 — Langues de communication */}
      <div className="prof-bloc">
        <div className="prof-bloc-title">🌍 Langues de communication *</div>
        <p className="prof-bloc-hint">Langues dans lesquelles vous pouvez enseigner</p>
        <div className="tags-grid">
          {['Français', 'Anglais', 'Arabe', 'Allemand', 'Espagnol', 'Italien', 'Néerlandais', 'Flamand', 'Portugais', 'Suédois', 'Danois', 'Norvégien', 'Russe', 'Turc'].map(l => (
            <button
              key={l}
              type="button"
              className={`tag-btn ${languesCommunication.includes(l) ? 'selected' : ''}`}
              onClick={() => toggleLangue(l)}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

    </div>
  );

  return (
    <div className="register-container">
      <BrowserRecommendation variant="compact" /> 
       <div className="register-card">
        <div className="register-header">
          <h2>🌟 First Arabic - Inscription</h2>
          {isProf && <div className="progress-bar">
            <div className={`step ${etapeProf >= 1 ? 'active' : ''}`}>1</div>
            <div className={`step ${etapeProf >= 2 ? 'active' : ''}`}>2</div>
          </div>}
        </div>

        <form onSubmit={handleRegister}>
          {!isProf || etapeProf === 1 ? renderEtapeBase() : renderEtapeProf()}

          {(!isProf || etapeProf === 2) && (
            <button 
              type="submit" 
              className="btn-submit"
              disabled={isSubmitting || !prenom || !nom || !email || !password || !telephone}
            >
              {isSubmitting ? "⏳ Inscription en cours..." : 
               role === "eleve" ? "🎓 Créer mon compte élève" : "👨‍🏫 Soumettre ma candidature"}
            </button>
          )}
        </form>

        {error && <div className="error-message">{error}</div>}

        <div className="register-footer">
          <p>
            Vous avez déjà un compte ? 
            <a href={role === "prof" ? "/professor-login" : "/login"}>Se connecter</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
